import {
  ArrowAsc,
  ArrowDesc,
  CheckedCheckboxIcon,
  DefaultSortIcon,
  DownloadIcon,
  MoreIcon,
  UnCheckCheckedCheckboxIcon,
  UncheckedCheckboxIcon,
} from 'assets/icons';
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TooltipProps,
  styled,
  tableCellClasses,
  tooltipClasses,
} from '@mui/material';
import React, {FC, memo, useState} from 'react';
import {TableComponents, TableVirtuoso} from 'react-virtuoso';
import {
  styledTableBody,
  styledTableHead,
  styledTableRow,
  tableContainer,
  tableHeadCell,
} from 'styles/MUIStyles/table';
import {
  tableMoreDotsMenu,
  tableMoreDotsMenuPaper,
} from 'styles/MUIStyles/common';

import {IGetAllBackupsListModified} from 'store/models/backups';
import {IMetadata} from 'store/models/common';
import {ISortConfig} from 'hooks/useSortColumn';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import dayjs from 'dayjs';
import {downloadFile} from 'core/utils';
import styles from '../../Backups.module.scss';
import {useGetNumberOfElements} from 'hooks';
import TableEmptyState from 'components/TableEmptyState/TableEmptyState';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: styledTableHead,
  borderBottom: '1px solid var(--border)',
  [`&.${tableCellClasses.body}`]: styledTableBody,
}));
const StyledTableRow = styled(TableRow)(() => styledTableRow);
const CustomWidthTooltip = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} classes={{popper: className}} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    wordBreak: 'normal',
  },
});

interface Props {
  modifiedAllBackups: IGetAllBackupsListModified[];
  setModifiedAllBackups: (allBackups: IGetAllBackupsListModified[]) => void;
  sortConfig: ISortConfig;
  handleColumnClick: (column: string) => void;
  allBackupsDebouncedSearch: string;
  metaData: IMetadata | null;
  downloadAllBackupsFileLoading: boolean;
  setDownloadAllBackupsLoading: (isLoading: boolean) => void;
}

const VirtuosoTableComponents: TableComponents<any> = {
  EmptyPlaceholder: () => <TableEmptyState />,
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer
      component={Paper}
      {...props}
      ref={ref}
      sx={tableContainer}
    />
  )),
  Table: (props) => <Table {...props} sx={{borderCollapse: 'separate'}} />,
  TableRow: ({item: _item, ...props}) => {
    return (
      <StyledTableRow
        {...props}
        key={_item.id}
        sx={{
          backgroundColor: _item.isChecked ? 'var(--bg-selected)' : 'var(--bg-surface)',
          '&:hover': {
            backgroundColor: _item.isChecked ? 'var(--bg-selected)' : 'var(--bg-subtle)',
          },
        }}
      />
    );
  },
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

const columns: {
  label: string;
  dataKey: string;
}[] = [
  {label: 'checkbox', dataKey: 'checkbox'},
  {label: '№', dataKey: 'number'},
  {label: 'File name', dataKey: 'fileName'},
  {label: 'Size', dataKey: 'size'},
  {label: 'Type', dataKey: 'type'},
  {label: 'Created Date Time', dataKey: 'createdDateTime'},
  {label: 'PosChassis Number', dataKey: 'posChassicNumber'},
  {label: 'Stations', dataKey: 'stationName'},
  {label: 'Task name', dataKey: 'commandName'},
  {label: '', dataKey: 'moreDots'},
];

const headerContent = (
  modifiedAllBackups: IGetAllBackupsListModified[],
  toggleAllCheckboxes: (isChecked: boolean) => void,
  getArrowForSortDirection: (column: string) => React.ReactNode,
  handleColumnClick: (column: string) => void,
) => {
  return (
    <TableRow>
      {columns.map((column) => {
        switch (column.label) {
          case 'checkbox': {
            return (
              <StyledTableCell key={column.dataKey} sx={{width: '1%'}}>
                <Box sx={tableHeadCell}>
                  <span>
                    {modifiedAllBackups.length > 0 &&
                      (modifiedAllBackups.every(
                        (backup) => backup.isChecked,
                      ) ? (
                        <CheckedCheckboxIcon
                          onClick={() => toggleAllCheckboxes(false)}
                        />
                      ) : modifiedAllBackups.some(
                          (backup) => backup.isChecked,
                        ) ? (
                        <UnCheckCheckedCheckboxIcon
                          onClick={() => toggleAllCheckboxes(false)}
                        />
                      ) : (
                        <UncheckedCheckboxIcon
                          onClick={() => toggleAllCheckboxes(true)}
                        />
                      ))}
                  </span>
                </Box>
              </StyledTableCell>
            );
          }
          case '№': {
            return (
              <StyledTableCell key={column.dataKey} sx={{width: '4%'}}>
                <Box sx={tableHeadCell}>
                  <span>№</span>
                </Box>
              </StyledTableCell>
            );
          }
          case 'File name': {
            return (
              <StyledTableCell key={column.dataKey} sx={{width: '12%'}}>
                <Box
                  sx={tableHeadCell}
                  onClick={() => handleColumnClick('fileName')}
                >
                  <span>File name</span>
                  {getArrowForSortDirection('fileName')}
                </Box>
              </StyledTableCell>
            );
          }
          case 'Size': {
            return (
              <StyledTableCell key={column.dataKey} sx={{width: '10%'}}>
                <Box
                  sx={tableHeadCell}
                  onClick={() => handleColumnClick('size')}
                >
                  <span>Size</span>
                  {getArrowForSortDirection('size')}
                </Box>
              </StyledTableCell>
            );
          }
          case 'Type': {
            return (
              <StyledTableCell key={column.dataKey} sx={{width: '5%'}}>
                <Box
                  sx={tableHeadCell}
                  onClick={() => handleColumnClick('type')}
                >
                  <span>Type</span>
                  {getArrowForSortDirection('type')}
                </Box>
              </StyledTableCell>
            );
          }
          case 'Created Date Time': {
            return (
              <StyledTableCell
                key={column.dataKey}
                sx={{width: '20%'}}
                onClick={() => handleColumnClick('createdDateTime')}
              >
                <Box sx={tableHeadCell}>
                  <span>Created date time</span>
                  {getArrowForSortDirection('createdDateTime')}
                </Box>
              </StyledTableCell>
            );
          }
          case 'PosChassis Number': {
            return (
              <StyledTableCell
                key={column.dataKey}
                sx={{width: '20%'}}
                onClick={() => handleColumnClick('posChassicNumber')}
              >
                <Box sx={tableHeadCell}>
                  <span>PosChassis Number</span>
                  {getArrowForSortDirection('posChassicNumber')}
                </Box>
              </StyledTableCell>
            );
          }
          case 'Stations': {
            return (
              <StyledTableCell
                key={column.dataKey}
                sx={{width: '11%'}}
                onClick={() => handleColumnClick('stationName')}
              >
                <Box sx={tableHeadCell}>
                  <span>Stations</span>
                  {getArrowForSortDirection('stationName')}
                </Box>
              </StyledTableCell>
            );
          }
          case 'Task name': {
            return (
              <StyledTableCell
                key={column.dataKey}
                sx={{width: '17%'}}
                onClick={() => handleColumnClick('commandName')}
              >
                <Box sx={tableHeadCell}>
                  <span>Task name</span>
                  {getArrowForSortDirection('commandName')}
                </Box>
              </StyledTableCell>
            );
          }
          case '': {
            return (
              <StyledTableCell key={column.dataKey} sx={{width: '1%'}}>
                <Box sx={tableHeadCell}>{null}</Box>
              </StyledTableCell>
            );
          }
          default: {
            return null;
          }
        }
      })}
    </TableRow>
  );
};

const rowContent = (
  index: number,
  backup: IGetAllBackupsListModified,
  toggleCheckbox: (backupId: number) => void,
  itemsOnPage: number[],
  setClickedBackup: (clickedBackup: IGetAllBackupsListModified | null) => void,
  handleMoreDotsClick: (event: React.MouseEvent<HTMLButtonElement>) => void,
  isMenuOpen: boolean,
) => {
  return (
    <React.Fragment>
      {columns.map((column) => {
        switch (column.label) {
          case 'checkbox': {
            return (
              <StyledTableCell key={column.dataKey}>
                <Checkbox
                  style={{padding: 0}}
                  key={column.dataKey}
                  checked={backup.isChecked}
                  name="allBackupCheckbox"
                  onChange={() => toggleCheckbox(backup.id)}
                  icon={<UncheckedCheckboxIcon />}
                  checkedIcon={<CheckedCheckboxIcon />}
                />
              </StyledTableCell>
            );
          }
          case '№': {
            return (
              <StyledTableCell key={column.dataKey}>
                {itemsOnPage.length ? itemsOnPage[index] : null}
              </StyledTableCell>
            );
          }
          case 'File name': {
            return (
              <StyledTableCell key={column.dataKey}>
                {backup.fileName}
              </StyledTableCell>
            );
          }
          case 'Size': {
            return (
              <StyledTableCell key={column.dataKey}>
                {backup.convertedSize}
              </StyledTableCell>
            );
          }
          case 'Type': {
            return (
              <StyledTableCell key={column.dataKey}>
                {backup.type}
              </StyledTableCell>
            );
          }
          case 'Created Date Time': {
            return (
              <StyledTableCell key={column.dataKey}>
                {backup.createdDateTime
                  ? dayjs(backup.createdDateTime).format('DD.MM.YYYY HH:mm')
                  : null}
              </StyledTableCell>
            );
          }
          case 'PosChassis Number': {
            return (
              <StyledTableCell key={column.dataKey}>
                {backup.posChassicNumber}
              </StyledTableCell>
            );
          }
          case 'Stations': {
            return (
              <StyledTableCell key={column.dataKey}>
                {backup.stationName}
              </StyledTableCell>
            );
          }
          case 'Task name': {
            return (
              <StyledTableCell key={column.dataKey}>
                <Box sx={{display: 'inline-block'}}>
                  <CustomWidthTooltip title={backup.commandName} arrow>
                    <span className={styles.allBackupDirectory}>
                      {backup.commandName}
                    </span>
                  </CustomWidthTooltip>
                </Box>
              </StyledTableCell>
            );
          }
          case '': {
            return (
              <StyledTableCell key={column.dataKey}>
                <Button
                  id="basic-button"
                  aria-controls={isMenuOpen ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={isMenuOpen ? 'true' : undefined}
                  onClick={(event) => {
                    handleMoreDotsClick(event);
                    setClickedBackup(backup);
                  }}
                >
                  <MoreIcon />
                </Button>
              </StyledTableCell>
            );
          }
          default: {
            return null;
          }
        }
      })}
    </React.Fragment>
  );
};

const AllBackupsTable: FC<Props> = memo((props) => {
  const itemsOnPage = useGetNumberOfElements(props.metaData);
  const [clickedBackup, setClickedBackup] =
    useState<IGetAllBackupsListModified | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);

  const handleMoreDotsClose = () => setAnchorEl(null);
  const handleMoreDotsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const getArrowForSortDirection = (column: string) => {
    if (props.sortConfig.column !== column) {
      return <DefaultSortIcon />;
    }

    return props.sortConfig.direction === 'asc' ? <ArrowAsc /> : <ArrowDesc />;
  };

  const toggleCheckbox = (backupId: number) => {
    props.setModifiedAllBackups(
      props.modifiedAllBackups.map((backup) => {
        if (backup.id === backupId) {
          return {...backup, isChecked: !backup.isChecked};
        }

        return backup;
      }),
    );
  };

  const toggleAllCheckboxes = (isChecked: boolean) => {
    props.setModifiedAllBackups(
      props.modifiedAllBackups.map((backup) => ({
        ...backup,
        isChecked,
      })),
    );
  };

  return (
    <>
      <TableVirtuoso
        data={props.modifiedAllBackups}
        components={VirtuosoTableComponents}
        fixedHeaderContent={() =>
          headerContent(
            props.modifiedAllBackups,
            toggleAllCheckboxes,
            getArrowForSortDirection,
            props.handleColumnClick,
          )
        }
        itemContent={(index, backup) => {
          return rowContent(
            index,
            backup,
            toggleCheckbox,
            itemsOnPage,
            setClickedBackup,
            handleMoreDotsClick,
            isMenuOpen,
          );
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMoreDotsClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
          sx: {
            padding: '4px 8px 4px 8px',
          },
        }}
        sx={tableMoreDotsMenuPaper}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
      >
        <MenuItem
          sx={tableMoreDotsMenu}
          onClick={() => {
            handleMoreDotsClose();
            if (clickedBackup) {
              downloadFile(
                clickedBackup.fileLink,
                props.setDownloadAllBackupsLoading,
              );
            }
          }}
        >
          <DownloadIcon />
          <span>Download</span>
        </MenuItem>
      </Menu>
    </>
  );
});

export default AllBackupsTable;
